import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { HubConnectionFactory } from './hub-connection.factory';
import { SignalRHubNames } from './signalr-hub-names';

export interface NotificationCreatedEvent {
  notificationId: string;
  type: string;
  title: string;
  body: string;
  deepLinkUrl: string | null;
}

/**
 * Connection lifecycle plus the one server-to-client event (Phase 5) for NotificationsHub:
 * NotificationCreated -- mirrors TicketingHubService. NotificationsHub joins a
 * user:{userId} group server-side, so every event received here is already scoped to the
 * current user; no client-side filtering is needed.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsHubService {
  private readonly connection: HubConnection = inject(HubConnectionFactory).create(SignalRHubNames.notifications);

  private readonly notificationCreatedSubject = new Subject<NotificationCreatedEvent>();
  private readonly reconnectingSubject = new Subject<void>();
  private readonly reconnectedSubject = new Subject<void>();
  private readonly closedSubject = new Subject<void>();

  readonly notificationCreated$ = this.notificationCreatedSubject.asObservable();

  /** Agent Workspace phase's Connection Recovery / Offline Detection (Stream 8) --
   * NotificationsHubService's connection is the one guaranteed to be live for the whole
   * authenticated session (started once from LayoutComponent), so ConnectionStatusService
   * piggybacks on its lifecycle events rather than every page's own hub connection needing
   * to track this independently. */
  readonly reconnecting$ = this.reconnectingSubject.asObservable();
  readonly reconnected$ = this.reconnectedSubject.asObservable();
  readonly closed$ = this.closedSubject.asObservable();

  constructor() {
    this.connection.on('NotificationCreated', (payload: NotificationCreatedEvent) => this.notificationCreatedSubject.next(payload));
    this.connection.onreconnecting(() => this.reconnectingSubject.next());
    this.connection.onreconnected(() => this.reconnectedSubject.next());
    this.connection.onclose(() => this.closedSubject.next());
  }

  async start(): Promise<void> {
    if (this.connection.state === HubConnectionState.Disconnected) {
      await this.connection.start();
    }
  }

  async stop(): Promise<void> {
    await this.connection.stop();
  }
}
